// components/comparison/BiodiversityNaturalChart.jsx
import React, { useMemo, useState, useEffect } from "react";
import NativeAnalysis from "../NativeAnalysis";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Label,
} from "recharts";

function getChart(comparisonData, pathwayName, chartKey) {
  const node = comparisonData?.[pathwayName];
  return node?.charts?.[chartKey] || node?.[chartKey] || null;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const sorted = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : -Infinity;
    const bv = typeof b.value === "number" ? b.value : -Infinity;
    return bv - av;
  });

  const getDash = (k) => {
    if (k === "Target Share") return "5 5";
    return "";
  };

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sorted.map((item) => {
        const dash = getDash(item.dataKey);
        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
                <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" strokeDasharray={dash} />
              </svg>
              <span className="text-[#7E8081]">{item.name}</span>
            </div>

            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("es-ES", { maximumFractionDigits: 2 })
                : item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Legend clickable SOLO para series togglables.
 * Las series fijas se muestran como "disabled" (no clic).
 */
function ClickableLegend({ payload, hiddenKeys, onToggle, isFixedKey }) {
  if (!payload?.length) return null;

  const getDash = (k) => {
    if (k === "Target Share") return "5 5";
    return "";
  };

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const fixed = isFixedKey(entry.dataKey);
        const isHidden = hiddenKeys.has(entry.dataKey);
        const dash = getDash(entry.dataKey);

        // Fijas: no botón
        if (fixed) {
          return (
            <div
              key={entry.dataKey}
              className="flex items-center gap-2"
              title="Serie fija"
            >
              <span className="text-[12px]">{entry.value}</span>
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color} strokeWidth="2" strokeDasharray={dash} />
              </svg>
            </div>
          );
        }

        // Toggable
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry.dataKey)}
            className={`flex items-center gap-2 ${isHidden ? "opacity-40" : ""}`}
            title="Click para mostrar/ocultar"
          >
            <span className="text-[12px]">{entry.value}</span>
            <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
              <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color} strokeWidth="2" strokeDasharray={dash} />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

const FIXED_KEYS = new Set(["Target Share"]);

export default function BiodiversityNaturalChart({
  comparisonData,
  selectedPathways,
  getColorByName,
  xLabel = "Year",
  yLabel = "Share (%)",
}) {
  // ✅ ocultar/mostrar SOLO computed por pathway
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const toggleKey = (k) => {
    // si por alguna razón intentan togglear fijo, lo ignoramos
    if (FIXED_KEYS.has(k)) return;

    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const { rows, keys } = useMemo(() => {
    if (!selectedPathways?.length) return { rows: [], keys: [] };

    const first = selectedPathways[0];
    const base = getChart(comparisonData, first, "chartbio1");
    const labels = base?.labels || [];

    // datasets base para series fijas
    const baseTarget = base?.datasets?.find((d) => d.label === "Target Share");

    const rows = labels.map((year, idx) => {
      const row = { year };

      // ✅ series fijas (una sola vez)
      if (baseTarget) row["Target Share"] = baseTarget.data?.[idx] ?? null;

      // ✅ series por pathway (solo computed)
      for (const p of selectedPathways) {
        const c = getChart(comparisonData, p, "chartbio1");
        if (!c?.labels?.length) continue;

        const ds = c.datasets?.find((d) => d.label === "Computed Share");
        if (!ds) continue;

        row[`${p}`] = ds.data?.[idx] ?? null;
      }

      return row;
    });

    // keys: fijas + computed por pathway
    const keys = [];
    if (baseTarget) keys.push("Target Share");

    for (const p of selectedPathways) {
      const c = getChart(comparisonData, p, "chartbio1");
      if (c?.datasets?.some((d) => d.label === "Computed Share")) {
        keys.push(`${p}`);
      }
    }

    return { rows, keys };
  }, [comparisonData, selectedPathways]);

  const [analysis, setAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    if (!rows?.length) return;

    // Only generate GPT descriptions when there are at least two pathways to compare
    if (!selectedPathways || selectedPathways.length < 2) {
      setAnalysis("Please select at least two pathways to compare analysis.");
      return;
    }
    
    let isMounted = true;
    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const payload = {
          context: "biodiversity_natural",
          dataset: rows,
        };
        const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/chat/fablegpt-comparison`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Analysis API error");
        const resData = await response.json();
        if (isMounted) setAnalysis(resData.analysis?.replace(/_/g, " ") || "No analysis available.");
      } catch (error) {
        console.error("Error fetching comparison analysis:", error);
        if (isMounted) setAnalysis("Cannot load analysis at this time.");
      } finally {
        if (isMounted) setLoadingAnalysis(false);
      }
    };
    
    fetchAnalysis();

    return () => { isMounted = false; };
  }, [rows, selectedPathways]);

  if (!rows?.length || !keys?.length) {
    return (
      <div className="rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200 text-sm text-slate-700">
        No data for Biodiversity (natural processes).
      </div>
    );
  }

  const isFixedKey = (k) => FIXED_KEYS.has(String(k));

  const strokeForKey = (k) => {
    // fijas: color neutral (ajústalo si quieres)
    if (k === "Target Share") return "#ff0000";

    // computed: color por pathway
    const pathway = String(k).split(" — ")[0];
    return getColorByName(pathway);
  };

  const dashForKey = (k) => {
    if (k === "Target Share") return "5 5";           // dashed
    // computed
    return ""; // línea sólida
  };

  const nameForKey = (k) => {
    if (k === "Target Share") return "Target Share";
    return k; // ya viene "pathway — Computed Share"
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 ring-1 ring-[#E3E5E6] shadow-sm">
      <div className="text-[14px] font-semibold text-[#101010] mb-3">
        Land where natural processes predominate
      </div>

      <div style={{ width: "100%", height: 440 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 30, right: 40, left: 20, bottom: 40 }}>
            <CartesianGrid stroke="#F1F3F4" strokeDasharray="3 3" vertical={false} />

            <XAxis
              dataKey="year"
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={12}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value={xLabel}
                position="insideBottom"
                offset={-30}
                style={{ fill: "#98999A", fontSize: 14, fontWeight: 600 }}
              />
            </XAxis>

            <YAxis
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={10}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value={yLabel}
                angle={-90}
                position="insideLeft"
                style={{ fill: "#98999A", fontSize: 14, fontWeight: 600 }}
              />
            </YAxis>

            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ paddingBottom: 8 }}
              content={(props) => (
                <ClickableLegend
                  {...props}
                  hiddenKeys={hiddenKeys}
                  onToggle={toggleKey}
                  isFixedKey={isFixedKey}
                />
              )}
            />

            {keys.map((k) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                name={nameForKey(k)}
                stroke={strokeForKey(k)}
                strokeDasharray={dashForKey(k)}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls
                isAnimationActive={false}
                hide={!isFixedKey(k) && hiddenKeys.has(k)} // ✅ fijas nunca se ocultan
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full min-h-[100px] mt-4">
        {loadingAnalysis ? (
          <div className="flex items-center gap-2 text-[#7E8081] text-sm animate-pulse">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating comparative analysis...
          </div>
        ) : (
          <div className="group relative">
            <NativeAnalysis content={analysis} />
          </div>
        )}
      </div>
    </div>
  );
}